import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';

/**
 * Create a new sales receipt/ticket
 * @param {Object} ticketData - Ticket data matching CreateTicketDto
 * @returns {Promise<Object>} Created ticket
 */
export const createTicket = async (ticketData) => {
  const response = await axiosInstance.post('Ticket/create-sale-ticket', ticketData);
  return response.data;
};

/**
 * Get all tickets
 * @returns {Promise<Array>} List of all tickets
 */
export const getAllTickets = async () => {
  const response = await axiosInstance.get('Ticket');
  return response.data;
};

/**
 * Get ticket by ID
 * @param {number} id - Ticket ID
 * @returns {Promise<Object>} Ticket details
 */
export const getTicketById = async (id) => {
  const response = await axiosInstance.get(`Ticket/${id}`);
  return response.data;
};

/**
 * Get ticket by ticket number
 * @param {string} ticketNumber - Ticket number (e.g., TKT-12345678-123)
 * @returns {Promise<Object>} Ticket details
 */
export const getTicketByNumber = async (ticketNumber) => {
  const response = await axiosInstance.get(`Ticket/by-number/${ticketNumber}`);
  return response.data;
};

/**
 * Get tickets by customer ID
 * @param {number} customerId - Customer ID
 * @returns {Promise<Array>} List of customer tickets
 */
export const getTicketsByCustomer = async (customerId) => {
  const response = await axiosInstance.get(`Ticket/by-customer/${customerId}`);
  return response.data;
};

/**
 * Get tickets by date range
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {Promise<Array>} List of tickets in date range
 */
export const getTicketsByDateRange = async (startDate, endDate) => {
  const response = await axiosInstance.get('Ticket/by-date-range', {
    params: { startDate, endDate }
  });
  return response.data;
};

/**
 * Get my sales (current user's sales)
 * @returns {Promise<Array>} List of current user's tickets
 */
export const getMySales = async () => {
  const response = await axiosInstance.get('Ticket/my-sales');
  return response.data;
};

/**
 * Get today's sales
 * @returns {Promise<Array>} List of today's tickets
 */
export const getTodaysSales = async () => {
  const response = await axiosInstance.get('Ticket/today');
  return response.data;
};

/**
 * Update ticket (refund, add notes, etc.)
 * @param {number} id - Ticket ID
 * @param {Object} updateData - Update data matching UpdateTicketDto
 * @returns {Promise<Object>} Updated ticket
 */
export const updateTicket = async (id, updateData) => {
  const response = await axiosInstance.put(`Ticket/${id}`, {
    ...updateData,
    Id: id
  });
  return response.data;
};

/**
 * Delete ticket
 * @param {number} id - Ticket ID
 * @returns {Promise<void>}
 */
export const deleteTicket = async (id) => {
  await axiosInstance.delete(`Ticket/${id}`);
};

/**
 * Get sales statistics
 * @param {Date|string} startDate - Optional start date
 * @param {Date|string} endDate - Optional end date
 * @returns {Promise<Object>} Sales statistics
 */
export const getStatistics = async (startDate = null, endDate = null) => {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  
  const response = await axiosInstance.get('Ticket/statistics', { params });
  return response.data;
};

/**
 * Transform cart data to CreateTicketDto format
 * @param {Object} params - Parameters for ticket creation
 * @returns {Object} Formatted ticket data
 */
export const transformCartToTicketDto = ({
  cart,
  customer,
  selectedPaymentMethod,
  paidAmount,
  discount,
  customVATRate,
  receiptNumber,
  notes = ''
}) => {
  // Calculate discount amount
  const subtotal = cart.reduce((sum, item) => sum + item.Quantity * item.Price, 0);
  let discountAmount = 0;
  
  if (discount) {
    if (discount.type === 'percentage') {
      discountAmount = (subtotal * discount.value) / 100;
    } else {
      discountAmount = discount.value;
    }
  }

  // Transform line items
  const lineItems = cart.map(item => {
    const itemVAT = customVATRate !== null && customVATRate !== undefined 
      ? customVATRate 
      : (item.VAT || 21);

    // Calculate item discount if global discount exists
    let itemDiscount = 0;
    if (discount && discount.type === 'percentage') {
      itemDiscount = discount.value; // Store as percentage
    }

    return {
      ProductId: item.Id,
      Quantity: parseFloat(item.Quantity) || 1,
      UnitPrice: parseFloat(item.Price) || 0,
      Discount: itemDiscount,
      VAT: parseFloat(itemVAT)
    };
  });

  const ticketDto = {
    CustomerId: customer?.Id || null,
    CustomerName: customer?.Name || 'Walk-in Customer',
    PaymentMethod: selectedPaymentMethod || 'Cash',
    PaidAmount: parseFloat(paidAmount) || 0,
    DiscountAmount: discountAmount,
    Notes: notes || (discount?.reason ? `Discount: ${discount.reason}` : ''),
    ReceiptNumber: receiptNumber,
    LineItems: lineItems
  };

  return ticketDto;
};

/**
 * Create ticket from cart and print
 * @param {Object} params - All required parameters for ticket creation
 * @returns {Promise<Object>} Created ticket with print status
 */
export const createAndPrintTicket = async (params) => {
  try {
    // Transform cart to ticket DTO
    const ticketDto = transformCartToTicketDto(params);
    
    // Create ticket in database
    const createdTicket = await createTicket(ticketDto);
    
    return {
      success: true,
      ticket: createdTicket,
      message: 'Ticket created successfully'
    };
  } catch (error) {
    console.error('Error creating ticket:', error);
    return {
      success: false,
      error: error.message || 'Failed to create ticket',
      message: 'Failed to create ticket in database'
    };
  }
};

/**
 * Validate ticket data before submission
 * @param {Object} ticketData - Ticket data to validate
 * @returns {Object} Validation result
 */
export const validateTicketData = (ticketData) => {
  const errors = [];

  if (!ticketData.PaymentMethod) {
    errors.push('Payment method is required');
  }

  if (!ticketData.PaidAmount || ticketData.PaidAmount <= 0) {
    errors.push('Paid amount must be greater than 0');
  }

  if (!ticketData.LineItems || ticketData.LineItems.length === 0) {
    errors.push('At least one product is required');
  }

  if (ticketData.LineItems) {
    ticketData.LineItems.forEach((item, index) => {
      if (!item.ProductId) {
        errors.push(`Line item ${index + 1}: Product ID is required`);
      }
      if (!item.Quantity || item.Quantity <= 0) {
        errors.push(`Line item ${index + 1}: Quantity must be greater than 0`);
      }
      if (item.UnitPrice === undefined || item.UnitPrice < 0) {
        errors.push(`Line item ${index + 1}: Unit price must be positive`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Export all functions
export default {
  createTicket,
  getAllTickets,
  getTicketById,
  getTicketByNumber,
  getTicketsByCustomer,
  getTicketsByDateRange,
  getMySales,
  getTodaysSales,
  updateTicket,
  deleteTicket,
  getStatistics,
  transformCartToTicketDto,
  createAndPrintTicket,
  validateTicketData
};